import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../store';
import API from '../api';

export default function Redpack() {
  const { code } = useParams();
  const { user, refreshUser } = useApp();
  const nav = useNavigate();

  const [info, setInfo]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [result, setResult] = useState(null); // { cardsGot } 或 null
  const [err, setErr]       = useState('');

  useEffect(() => {
    API.get(`/api/redpack/info/${code}`)
      .then(d => setInfo(d))
      .catch(() => setErr('红包不存在或已失效'))
      .finally(() => setLoading(false));
  }, [code]);

  async function handleClaim() {
    if (!user) {
      // 未登录：跳转登录，登录后回来
      sessionStorage.setItem('redpack_return', `/redpack/${code}`);
      nav('/');
      return;
    }
    setClaiming(true);
    setErr('');
    try {
      const r = await API.post(`/api/redpack/claim/${code}`);
      setResult({ cardsGot: r.cardsGot });
      await refreshUser();
      // 刷新红包信息
      const updated = await API.get(`/api/redpack/info/${code}`);
      setInfo(updated);
    } catch (e) {
      setErr(e.message || '抢红包失败');
    } finally {
      setClaiming(false);
    }
  }

  // 登录后自动检查是否需要回跳抢红包
  useEffect(() => {
    if (user) {
      const ret = sessionStorage.getItem('redpack_return');
      if (ret && ret === `/redpack/${code}`) {
        sessionStorage.removeItem('redpack_return');
      }
    }
  }, [user, code]);

  if (loading) return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.loadingText}>加载中...</div>
      </div>
    </div>
  );

  if (err && !info) return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.errorText}>{err}</div>
        <button style={styles.btn} onClick={() => nav('/lobby')}>返回大厅</button>
      </div>
    </div>
  );

  const alreadyClaimed = result !== null;
  const unavailable = info && (!info.available || alreadyClaimed);

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* 标题 */}
        <div style={styles.title}>{info?.title || '房卡红包'}</div>
        <div style={styles.creator}>来自 {info?.creator}</div>

        {/* 房卡图标 */}
        <div style={styles.iconWrap}>
          <div style={styles.cardIcon}>
            <span style={styles.cardIconText}>房卡</span>
          </div>
        </div>

        {/* 数量信息 */}
        {alreadyClaimed ? (
          <div style={styles.successWrap}>
            <div style={styles.successNum}>+{result.cardsGot}</div>
            <div style={styles.successLabel}>张房卡已到账</div>
          </div>
        ) : (
          <div style={styles.infoWrap}>
            <div style={styles.perClaimNum}>{info?.perClaim}</div>
            <div style={styles.perClaimLabel}>张 / 人</div>
            <div style={styles.progress}>
              {info?.claimedCount} / {info?.maxClaimers} 人已抢
            </div>
          </div>
        )}

        {/* 状态提示 */}
        {info?.expired && !alreadyClaimed && (
          <div style={styles.statusTip}>红包已过期</div>
        )}
        {info?.finished && !info?.expired && !alreadyClaimed && (
          <div style={styles.statusTip}>手慢了，红包已抢完</div>
        )}

        {/* 错误提示 */}
        {err && <div style={styles.errMsg}>{err}</div>}

        {/* 按钮 */}
        {!alreadyClaimed && (
          <button
            style={{
              ...styles.btn,
              ...(unavailable ? styles.btnDisabled : {}),
            }}
            onClick={handleClaim}
            disabled={unavailable || claiming}
          >
            {claiming ? '抢中...' :
             !user ? '登录后抢红包' :
             info?.finished ? '已抢完' :
             info?.expired ? '已过期' :
             '抢房卡'}
          </button>
        )}

        {alreadyClaimed && (
          <button style={styles.btn} onClick={() => nav('/lobby')}>
            去游戏大厅
          </button>
        )}

        {/* 有效期 */}
        {info?.expiresAt && (
          <div style={styles.expiry}>
            有效期至 {new Date(info.expiresAt).toLocaleString('zh-CN', { hour12: false })}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #0d1b3e 0%, #1a2d5a 60%, #0d1b3e 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    background: 'linear-gradient(160deg, #1e2e55 0%, #243560 100%)',
    borderRadius: 20,
    padding: '40px 32px',
    width: '100%',
    maxWidth: 360,
    textAlign: 'center',
    boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: '#F5C518',
    marginBottom: 6,
  },
  creator: {
    fontSize: 13,
    color: '#8BA8D4',
    marginBottom: 28,
  },
  iconWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 24,
  },
  cardIcon: {
    width: 88,
    height: 88,
    borderRadius: 18,
    background: 'linear-gradient(135deg, #F5C518, #e6a800)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 20px rgba(245,197,24,0.4)',
  },
  cardIconText: {
    fontSize: 18,
    fontWeight: 700,
    color: '#0d1b3e',
  },
  infoWrap: {
    marginBottom: 28,
  },
  perClaimNum: {
    fontSize: 56,
    fontWeight: 800,
    color: '#F5C518',
    lineHeight: 1,
  },
  perClaimLabel: {
    fontSize: 16,
    color: '#8BA8D4',
    marginTop: 4,
    marginBottom: 12,
  },
  progress: {
    fontSize: 14,
    color: '#8BA8D4',
    background: 'rgba(255,255,255,0.06)',
    borderRadius: 8,
    padding: '6px 16px',
    display: 'inline-block',
  },
  successWrap: {
    marginBottom: 28,
  },
  successNum: {
    fontSize: 64,
    fontWeight: 800,
    color: '#4caf50',
    lineHeight: 1,
  },
  successLabel: {
    fontSize: 18,
    color: '#8BA8D4',
    marginTop: 8,
  },
  statusTip: {
    fontSize: 15,
    color: '#8BA8D4',
    marginBottom: 20,
  },
  errMsg: {
    fontSize: 13,
    color: '#ff6b6b',
    marginBottom: 14,
    background: 'rgba(255,107,107,0.1)',
    borderRadius: 8,
    padding: '8px 12px',
  },
  btn: {
    width: '100%',
    padding: '16px 0',
    borderRadius: 12,
    border: 'none',
    background: 'linear-gradient(90deg, #F5C518, #e6a800)',
    color: '#0d1b3e',
    fontSize: 18,
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: 8,
    marginBottom: 16,
  },
  btnDisabled: {
    background: '#2a3a5a',
    color: '#4a5a7a',
    cursor: 'not-allowed',
  },
  loadingText: {
    color: '#8BA8D4',
    fontSize: 16,
    padding: 40,
  },
  errorText: {
    color: '#ff6b6b',
    fontSize: 16,
    marginBottom: 20,
  },
  expiry: {
    fontSize: 12,
    color: '#4a5a7a',
    marginTop: 4,
  },
};
